import React from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from "@mui/material";
import { useGetLogsDataQuery } from "../../Service/admin";
import { styled } from "@mui/system";

const formatKey = (key) => {
  let formattedKey = key.replace(
    /_([a-z])/g,
    (_, letter) => ` ${letter.toUpperCase()}`
  );
  formattedKey = formattedKey.replace(
    /__([a-z])/g,
    (_, letter) => `-${letter.toUpperCase()}`
  );
  return formattedKey.charAt(0).toUpperCase() + formattedKey.slice(1);
};

const formatData = (data) => {
  if (Array.isArray(data)) {
    return data.map(formatData);
  }

  if (typeof data === "object" && data !== null) {
    const formattedObj = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const formattedKey = formatKey(key);
        formattedObj[formattedKey] = formatData(data[key]);
      }
    }
    return formattedObj;
  }

  return data;
};

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: "20px",
  borderRadius: "8px",
  boxShadow: "inset 0px 4px 8px rgba(0, 0, 0, 0.1)",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1.5),
}));

const BoldTableCell = styled(StyledTableCell)({
  fontWeight: "bold",
  color: "#555",
});

const getMimeType = (url) => {
  if (typeof url !== "string") return "";

  try {
    const cleanUrl = new URL(url).pathname;
    const ext = cleanUrl?.split(".")?.pop()?.toLowerCase();
    const videoTypes = {
      mp4: "video/mp4",
      webm: "video/webm",
      ogg: "video/ogg",
    };
    const audioTypes = {
      mp3: "audio/mpeg",
      wav: "audio/wav",
      ogg: "audio/ogg",
    };
    return videoTypes[ext] || audioTypes[ext] || "";
  } catch {
    return "";
  }
};

const renderMedia = (type, src) => {
  const mimeType = getMimeType(src);

  if (!src) return null;
  if (type === "image") {
    return (
      <img
        src={src}
        alt="Media"
        style={{ maxWidth: "250px", borderRadius: 4 }}
      />
    );
  }
  if (type === "video" && mimeType.startsWith("video/")) {
    return (
      <video width="250" height="150" controls>
        <source src={src} type={mimeType} />
        Your browser does not support this video format.
      </video>
    );
  }
  if (type === "audio" && mimeType.startsWith("audio/")) {
    return (
      <audio controls style={{ width: "250px", height: "40px" }}>
        <source src={src} type={mimeType} />
        Your browser does not support this audio format.
      </audio>
    );
  }
  return (
    <a href={src} target="_blank" rel="noopener noreferrer">
      Download {type}
    </a>
  );
};

const renderValueContent = (key, value) => {
  const lowerKey = key.toLowerCase();

  if (["image", "video", "audio"].includes(lowerKey)) {
    const media = renderMedia(lowerKey, value);
    if (media) return media;
  }

  if (value !== null && value !== undefined && value !== "") {
    return typeof value === "object"
      ? renderDataInTable(value)
      : value.toString();
  }
  return "-";
};

const renderDataInTable = (data) => {
  if (typeof data === "object" && data !== null) {
    return Object.keys(data).map((key, index) => (
      <TableRow
        key={key}
        style={{ backgroundColor: index % 2 ? "#f9f9f9" : "#fff" }}
      >
        <BoldTableCell component="th" scope="row">
          {key}
        </BoldTableCell>
        <StyledTableCell>{renderValueContent(key, data[key])}</StyledTableCell>
      </TableRow>
    ));
  }
  return null;
};

const BOPPListModal = ({ docId }) => {
  const { data } = useGetLogsDataQuery({ docId });
  const formattedData = formatData(data?.docDetails);

  return (
    <div style={{ padding: "20px" }}>
      <Typography variant="h6" gutterBottom style={{ color: "#3f51b5" }}>
        BOPP Log Details :-
      </Typography>
      {formattedData ? (
        <StyledTableContainer component={Paper}>
          <Table>
            <TableBody>{renderDataInTable(formattedData)}</TableBody>
          </Table>
        </StyledTableContainer>
      ) : (
        <Typography variant="body2" color="textSecondary">
          No data available
        </Typography>
      )}
    </div>
  );
};

export default BOPPListModal;
